/**
 * Mobile Qwen3 WebLLM Integration for Browser
 */

// Import types
import type {
  BackendResponse,
  BackendChunk,
  TopJsonObject,
  GenerationOptions,
  StreamResult,
  ModelStats,
  TokenizerInterface
} from './types';

// Import WebLLM
import * as webllm from "@mlc-ai/web-llm";

import {
  MODEL_CONFIGS,
  ModelConfig,
  ModelUrlsResponse
} from './hybrid_model_selection';

// Type definitions for WebGPU and ONNX Runtime
declare global {
  interface Navigator {
    gpu?: GPU;
  }
  
  interface Window {
    AutoTokenizer: any;
    webllm?: typeof import('@mlc-ai/web-llm');
  }
}

type LocalQwenChatMessage = webllm.ChatCompletionMessageParam;

export interface LocalQwenRuntimeModelConfig {
  modelId: string;
  displayName: string;
  vramRequiredMB: number;
  contextWindowSize: number;
}

export const LOW_LOCAL_QWEN35_2B_RUNTIME_MODEL: LocalQwenRuntimeModelConfig = {
  modelId: 'qwen3.5-2b-q4f16-webllm',
  displayName: 'Qwen3.5-2B-Q4F16-WebLLM',
  vramRequiredMB: 2245,
  contextWindowSize: 4096,
};

export const TINY_LOCAL_QWEN35_08B_RUNTIME_MODEL: LocalQwenRuntimeModelConfig = {
  modelId: 'qwen3.5-0.8b-q4f16-webllm',
  displayName: 'Qwen3.5-0.8B-Q4F16-WebLLM',
  vramRequiredMB: 1629,
  contextWindowSize: 2048,
};

// Define GPUBufferUsage constants
const GPUBufferUsage = {
  STORAGE: 0x00000080,
  COPY_DST: 0x00000008,
  COPY_SRC: 0x00000004,
  MAP_READ: 0x00000001,
  MAP_WRITE: 0x00000002,
  INDEX: 0x00000010,
  VERTEX: 0x00000020,
  UNIFORM: 0x00000040,
  INDIRECT: 0x00000100,
  QUERY_RESOLVE: 0x00000200,
};

// Define GPUFeatureName type
type GPUFeatureName = string;

// WebGPU types - using 'any' to avoid type conflicts
interface GPU {
  requestAdapter(options?: any): Promise<GPUAdapter | null>;
}

interface GPUAdapter {
  requestDevice(descriptor?: any): Promise<GPUDevice>;
  features: Set<string>;
}

interface GPUDevice {
  createBuffer(descriptor: any): GPUBuffer;
  queue: GPUQueue;
  features: Set<string>;
}

interface GPUQueue {
  writeBuffer(buffer: GPUBuffer, bufferOffset: number, data: ArrayBuffer): void;
}

interface GPUBuffer {
  destroy(): void;
}

function detectDegenerateLocalOutput(text: string): { degenerate: boolean; reason: string } {
  const tail = String(text || '').slice(-500);
  const compactTail = tail.replace(/\s+/g, '');

  if (compactTail.length >= 40) {
    const bangCount = compactTail.split('!').length - 1;
    const questionCount = compactTail.split('?').length - 1;
    const repeatedPunctCount = Array.from(compactTail).filter((ch) => {
      return ch === '!' || ch === '?' || ch === '.' || ch === '#' || ch === '-' || ch === '_' || ch === '=' || ch === '*';
    }).length;

    if (bangCount >= 30 && bangCount / compactTail.length > 0.55) {
      return { degenerate: true, reason: 'exclamation_mark_loop' };
    }

    if (questionCount >= 30 && questionCount / compactTail.length > 0.55) {
      return { degenerate: true, reason: 'question_mark_loop' };
    }

    if (repeatedPunctCount >= 60 && repeatedPunctCount / compactTail.length > 0.70) {
      return { degenerate: true, reason: 'punctuation_loop' };
    }
  }

  const noThinkTail = tail.toLowerCase().replace(/\s+/g, '');
  if (noThinkTail.includes('<think></think>')) {
    const afterThink = noThinkTail.split('<think></think>').pop() || '';
    const afterThinkPunctCount = Array.from(afterThink).filter((ch) => {
      return ch === '!' || ch === '?' || ch === '.' || ch === '#' || ch === '-' || ch === '_' || ch === '=' || ch === '*';
    }).length;

    if (afterThink.length >= 40 && afterThinkPunctCount / afterThink.length > 0.70) {
      return { degenerate: true, reason: 'empty_think_then_punctuation_loop' };
    }
  }

  return { degenerate: false, reason: '' };
}

function cleanLowLocalQwenOutput(text: string): string {
  return String(text || '')
    .replace(/<think>\s*<\/think>/gi, '')
    .replace(/<think>[\s\S]*?<\/think>/gi, '')
    .trim();
}

/**
 * Main Mobile Qwen3 WebLLM class
 */
export class MobileQwen3WebLLM {
  protected engine: webllm.MLCEngine | null = null;
  protected tokenizer: TokenizerInterface | null = null;
  protected isInitialized: boolean = false;

  // Model Config Source
  protected modelUrl: string | null = null;
  protected wasmUrl: string | null = null;
  // protected configUrl?: string;        
  // protected tokenizerUrl?: string;       
  // protected tokenizerConfigUrl?: string;  

  protected progressCallback?: (loaded: number, total: number, percentage: number) => void;
  protected runtimeModel: LocalQwenRuntimeModelConfig;

  constructor(runtimeModel: LocalQwenRuntimeModelConfig = LOW_LOCAL_QWEN35_2B_RUNTIME_MODEL) {
    this.engine = null;
    this.tokenizer = null;
    this.isInitialized = false;
    this.modelUrl = null;
    this.wasmUrl = null;
    this.runtimeModel = runtimeModel;
  }

  /**
    * Initialize with signed URLs from backend
    */
  public async initializeWithSignedUrls(
    urls: ModelUrlsResponse,
    onProgress?: (loaded: number, total: number, percentage: number) => void
  ): Promise<boolean> {
    try {
      console.log('🚀 Initializing Mobile Qwen3.5 WebLLM with signed URLs...');

      this.modelUrl = urls.modelUrl;
      this.wasmUrl = urls.wasmUrl;
      // this.configUrl = urls.configUrl;      
      // this.tokenizerUrl = urls.tokenizerUrl;   
      // this.tokenizerConfigUrl = urls.tokenizerConfigUrl;
      this.progressCallback = onProgress;
    
      // Initialize WebLLM engine
      await this.loadModel();
        
      // Load tokenizer
      // await this.loadTokenizer();
    
      this.isInitialized = true;
      console.log('✅ Mobile Qwen3.5 WebLLM initialized successfully!');
        
      return true;
    } catch (error) {
      console.error('❌ Failed to initialize Mobile Qwen3.5 WebLLM:', error);
      return false;
    }
  }

  protected getModelConfigFromUrl(url: string): ModelConfig | null {
    for (const config of Object.values(MODEL_CONFIGS)) {
    if (config.url === url) {
    return config;
    }
    }
    return null;
  }

  // NEWLY CHANGED:
  protected async loadModel(): Promise<void> {
    if (!this.modelUrl) throw new Error('Model URL not set');
    if (!this.wasmUrl) throw new Error('WASM URL not set');
  
    try {
      const CUSTOM_MODEL_ID = this.runtimeModel.modelId;
  
      console.log('🔧 Creating model record...');
  
      // Step 1: Strip the SAS token from the model URL.
      // WebLLM's cleanModelUrl() calls new URL(modelUrl).href, which treats
      // the SAS token as query parameters. When WebLLM then resolves relative
      // file paths (e.g. "mlc-chat-config.json") against this base URL, the
      // SAS token is discarded — causing a 404 on every file fetch.
      // We must pass a clean base URL to WebLLM and re-attach the SAS token
      // via a fetch interceptor instead.
      const fullUrl = this.modelUrl;
      const questionMarkIndex = fullUrl.indexOf('?');
      const modelBaseUrl = questionMarkIndex !== -1
        ? fullUrl.substring(0, questionMarkIndex)
        : fullUrl;
      let sasToken = questionMarkIndex !== -1
        ? fullUrl.substring(questionMarkIndex + 1)
        : '';
  
      console.log('📦 Model base URL:', modelBaseUrl.substring(0, 100) + '...');
      console.log('📦 WASM URL:', this.wasmUrl.substring(0, 100) + '...');
  
      const modelRecord: webllm.ModelRecord = {
        model_id: CUSTOM_MODEL_ID,
        model: modelBaseUrl,     // Clean URL — no SAS token
        model_lib: this.wasmUrl, // WASM URL already has its own SAS token from backend
        vram_required_MB: this.runtimeModel.vramRequiredMB,
        low_resource_required: this.runtimeModel.modelId.includes('0.8b'),
        overrides: {
          context_window_size: this.runtimeModel.contextWindowSize,
          max_history_size: 1,
        },
      };
  
      const appConfig: webllm.AppConfig = {
        model_list: [modelRecord]
      };
  
      const engineConfig: webllm.MLCEngineConfig = {
        appConfig,
        initProgressCallback: (progress) => {
          if (this.progressCallback) {
            const pct = Math.round((progress?.progress ?? 0) * 100);
            this.progressCallback(pct, 100, pct);
          }
          if (progress?.text) {
            console.log('📊 Progress:', progress.text);
          }
        }
      };
  
      console.log('🚀 Creating MLCEngine...');
  
      // Step 2: Install a temporary fetch interceptor to re-attach the SAS
      // token to all Azure Blob Storage requests made by WebLLM.
      // WebLLM fetches mlc-chat-config.json, tokenizer.json, and all
      // params_shard_*.bin files via window.fetch. Without the SAS token,
      // every one of these requests returns 404.
      const originalFetch = window.fetch;
      const blobHost = new URL(fullUrl).origin;
  
      window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
        const url = typeof input === 'string'
          ? input
          : input instanceof URL
            ? input.href
            : (input as Request).url;
  
        // Re-attach SAS token only to Azure Blob requests that don't already have it
        if (url.startsWith(blobHost) && !url.includes('sig=')) {
          const separator = url.includes('?') ? '&' : '?';
          return originalFetch(url + separator + sasToken, init);
        }
        return originalFetch(input, init);
      };
  
      try {
        // Step 3: Create the engine — WebLLM will fetch all model files
        // through the intercepted fetch, which now includes the SAS token.
        this.engine = await webllm.CreateMLCEngine([CUSTOM_MODEL_ID], engineConfig);
      } finally {
        // Step 4: Always restore original fetch, even if engine creation fails.
        // Leaving the interceptor in place would break all other fetch calls.
        window.fetch = originalFetch;
      }
  
      console.log('✅ Model loaded successfully');
    } catch (err) {
      console.error('❌ Failed to load model:', err);
      throw err;
    }
  }

  /**
   * Generate response with streaming and proper typing
   */
  public async* generateStream(
    prompt: string | LocalQwenChatMessage[],
    options: GenerationOptions = {}
  ): AsyncGenerator<StreamResult, void, unknown> {
    if (!this.isInitialized || !this.engine) {
      throw new Error('Model not initialized');
    }

    const messages: LocalQwenChatMessage[] = Array.isArray(prompt)
      ? prompt
      : [{ role: "user", content: typeof prompt === 'string' ? prompt : '' } as LocalQwenChatMessage];

    const safePrompt = Array.isArray(prompt)
      ? messages.map((m) => String(m.content || '')).join('\n\n')
      : (typeof prompt === 'string' ? prompt : '');

    const {
      maxTokens = 2048,
      temperature = 0.7,
      topP = 0.9,
    } = options;

    console.log('🎯 Starting generation with WebLLM...');
    console.log('📝 Prompt length:', safePrompt.length);

    const startTime = performance.now();
    let totalTokens = 0;
    let fullText = '';

    try {
      try {
        if (typeof (this.engine as any).resetChat === 'function') {
          await (this.engine as any).resetChat(false);
        }
      } catch (resetErr) {
        console.warn('WebLLM resetChat failed before local task; continuing:', resetErr);
      }

      // Use WebLLM's chat completion with streaming
      const chunks = await (this.engine.chat.completions.create as any)({
        messages,
        temperature,
        top_p: topP,
        max_tokens: maxTokens,
        presence_penalty: 1.5,
        frequency_penalty: 0.0,
        stop: ['\n!!!!!!!!!!'],
        stream: true,
        extra_body: {
          enable_thinking: false,
        },
      });

      // Stream tokens
      for await (const chunk of chunks) {
        const delta = chunk.choices[0]?.delta?.content || '';

        if (delta) {
          totalTokens++;
          fullText += delta;

          const degenerate = detectDegenerateLocalOutput(fullText);
          if (degenerate.degenerate) {
            console.error('Low-local WebLLM degenerate output detected; interrupting generation.', {
              reason: degenerate.reason,
              chars: fullText.length,
              tail: fullText.slice(-500),
            });

            try {
              await this.engine.interruptGenerate();
            } catch (interruptErr) {
              console.warn('interruptGenerate failed after degenerate output detection:', interruptErr);
            }

            throw new Error(`LOW_LOCAL_DEGENERATE_OUTPUT:${degenerate.reason}`);
          }

          const inferenceTime = performance.now() - startTime;

          yield {
            token: delta,
            tokenId: 0, // WebLLM doesn't expose token IDs in streaming
            isComplete: false,
            inferenceTime,
            totalTokens
          };
        }
      }

      // Final result
      const totalTime = performance.now() - startTime;
      console.log(`✅ Generation complete: ${totalTokens} tokens in ${(totalTime / 1000).toFixed(2)}s`);
      console.log(`📊 Speed: ${(totalTokens / (totalTime / 1000)).toFixed(1)} tokens/sec`);

    } catch (error) {
      console.error('❌ Generation failed:', error);
      throw error;
    }
  }

  /**
   * Get model performance stats with proper typing
   */
  public getStats(): ModelStats {
    return {
      isInitialized: this.isInitialized,
      modelUrl: this.modelUrl,
      device: 'WebLLM',  // Changed: WebLLM manages device internally
      features: ['webgpu', 'mlc'],  // Changed: Static features
      memoryUsage: undefined  // Changed: WebLLM doesn't expose memory usage
    };
  }

  /**
   * Clean up resources
   */
  public dispose(): void {
    // WebLLM doesn't require manual disposal
    // Engine is managed by WebLLM runtime
    
    this.engine = null;  // Changed: Clear engine reference
    this.tokenizer = null;
    this.isInitialized = false;
    console.log('✅ Mobile Qwen3.5 WebLLM disposed');
  }  
}

/**
 * Extended child class for processor
 */
export class MobileQwen3WebLLMProcessor extends MobileQwen3WebLLM {
  private currentBackendData: BackendResponse | null = null;
  private chunkIdMap: Map<string, string> = new Map();
  private finalHtmlStructure: string[] = [];
  
  // NEW: Segment streaming state
  private processedSegmentNumbers: Set<number> = new Set();
  private lastYieldedSegmentNumber: number = 0;
  private allChunks: BackendChunk[] = [];
  private task1ResultsSection: string = '';

  private shownWarnings: Set<string> = new Set();
  private lastExtractedCount: number = 0;

  private buildMessages(systemPromptOrCombinedPrompt: string, userPrompt?: string): LocalQwenChatMessage[] | string {
    if (typeof userPrompt === 'string' && userPrompt.trim().length > 0) {
      const messages: LocalQwenChatMessage[] = [];

      if (systemPromptOrCombinedPrompt && systemPromptOrCombinedPrompt.trim().length > 0) {
        messages.push({ role: 'system', content: systemPromptOrCombinedPrompt } as LocalQwenChatMessage);
      }

      messages.push({ role: 'user', content: userPrompt } as LocalQwenChatMessage);
      return messages;
    }

    return systemPromptOrCombinedPrompt;
  }

  // RESTORE: for metadata handling:
  private contextMap: Map<string, TopJsonObject> = new Map();

  // For apply_rewrites functionality
  private userQuestion: string = '';
  protected tokenizer: any = null;

  protected modelUrl: string = '';
  protected progressCallback?: (loaded: number, total: number, percentage: number) => void;


  constructor(runtimeModel: LocalQwenRuntimeModelConfig = LOW_LOCAL_QWEN35_2B_RUNTIME_MODEL) {
    super(runtimeModel);
  }

  private assertPromptReady(taskName: string, prompt: string): void {
    const chars = typeof prompt === 'string' ? prompt.length : 0;
    const trimmed = typeof prompt === 'string' ? prompt.trim() : '';

    if (trimmed.length < 20) {
      console.error(`[${taskName}] Refusing empty local WebLLM prompt`, {
        chars,
        preview: prompt,
      });
      throw new Error(`[${taskName}] Empty or invalid prompt; local WebLLM generation aborted.`);
    }
  }

  private taskMaxTokens(taskName: 'task3' | 'task4' | 'task5' | 'task6'): number {
    const isTiny = this.runtimeModel.modelId.includes('0.8b');

    if (!isTiny) {
      switch (taskName) {
        case 'task3': return 1024;
        case 'task4': return 2048;
        case 'task5': return 2048;
        case 'task6': return 192;
      }
    }

    switch (taskName) {
      case 'task3': return 512;
      case 'task4': return 768;
      case 'task5': return 768;
      case 'task6': return 192;
    }
  }

  /**
   * Clear cached models (useful for debugging or forcing re-download)
   */
  public async clearCache(): Promise<void> {
    try {
      const CACHE_NAME = 'enlivesystems-models-v5';
      const deleted = await caches.delete(CACHE_NAME);
      if (deleted) {
        console.log('✅ Model cache cleared');
      } else {
        console.log('ℹ️  No cache to clear');
      }
    } catch (error) {
      console.error('❌ Failed to clear cache:', error);
    }
  }

  /**
   * Check if model is cached
   */
  public async isModelCached(): Promise<boolean> {
    try {
      const CACHE_NAME = 'enlivesystems-models-v5';
      const CACHE_VERSION = 'qwen3.5-2b-q4f16-webllm-v1';
      const cacheKey = `${this.modelUrl}?v=${CACHE_VERSION}`;
      
      const cache = await caches.open(CACHE_NAME);
      const cachedResponse = await cache.match(cacheKey);
      
      return cachedResponse !== undefined;
    } catch (error) {
      return false;
    }
  }

  /**
   * Execute Task #3: Generate expanded queries for better context retrieval
   * Returns the RAW LLM response for backend parsing
   */
  public async executeTask3(task3PromptOrSystem: string, task3UserPrompt?: string): Promise<string> {
    const promptForGeneration = this.buildMessages(task3PromptOrSystem, task3UserPrompt);
    const task3PromptChars = task3PromptOrSystem.length + (task3UserPrompt?.length || 0);
    this.assertPromptReady('Task 3', task3UserPrompt || task3PromptOrSystem);
    console.log('🚀 Executing Task #3: Query Expansion');
    console.log(`📝 Prompt length: ${task3PromptChars} chars`);
    
    let fullResponse = '';
    
    // Generate expanded queries using Mobile Qwen3.5
    for await (const result of this.generateStream(promptForGeneration, {
      temperature: 0.7,       // Kept at 0.7 for more creativity
      topP: 0.9,
      maxTokens: this.taskMaxTokens('task3')
    })) {
      fullResponse += result.token;
    }
    
    console.log('✅ Task #3 generation complete');
    console.log(`📄 Raw response length: ${fullResponse.length} chars`);
    
    // Return RAW response for backend to parse, minus empty Qwen think tags.
    return fullResponse.replace(/<think>\s*<\/think>/gi, '').trim();
  }
  
  /**
   * Execute Task #4: Generate an enhanced query from context.
   * 
   * Similar to executeTask3 but with different generation parameters
   * optimized for query rewriting rather than query expansion.
   * 
   * Returns the RAW LLM response (the enhanced query string).
   */
  public async executeTask4(task4PromptOrSystem: string, task4UserPrompt?: string): Promise<string> {
    let task4Prompt = task4UserPrompt || task4PromptOrSystem;
    this.assertPromptReady('Task 4', task4Prompt);
    console.log('🚀 Executing Task #4: Profile Insights Generation');
    console.log(`📝 Prompt length: ${task4Prompt.length} chars`);
    
    // ── PRE-FLIGHT TOKEN BUDGET CHECK ──
    // Context window is model-specific: 4096 for 2B, 2048 for tiny 0.8B.
    // Input and output share the same context window.
    const CHARS_PER_TOKEN = 3.5;
    const RESERVED_OUTPUT_TOKENS = this.taskMaxTokens('task4');
    const SAFETY_TOKENS = 128;
    const MAX_INPUT_TOKENS = Math.max(
      256,
      this.runtimeModel.contextWindowSize - RESERVED_OUTPUT_TOKENS - SAFETY_TOKENS
    );
    const MAX_INPUT_CHARS = Math.round(MAX_INPUT_TOKENS * CHARS_PER_TOKEN);

    if (task4Prompt.length > MAX_INPUT_CHARS) {
      console.warn(
        `⚠️ [Task 4] Prompt (${task4Prompt.length} chars / ~${Math.round(task4Prompt.length / CHARS_PER_TOKEN)} tokens) ` +
        `exceeds estimated budget (${MAX_INPUT_CHARS} chars / ~${MAX_INPUT_TOKENS} tokens). Truncating to fit context window.`
      );

      let truncated = task4Prompt.substring(0, MAX_INPUT_CHARS);

      // Try to cut cleanly after the last complete </source_document> tag
      const lastClosingTag = truncated.lastIndexOf('</source_document>');
      if (lastClosingTag > truncated.length * 0.5) {
        truncated = truncated.substring(0, lastClosingTag + '</source_document>'.length);
        console.log(`📏 [Task 4] Truncated at last complete </source_document> tag (${truncated.length} chars)`);
      }

      // Re-append the query/findings trigger if it was cut off
      if (!truncated.includes('Findings:')) {
        const findingsIdx = task4Prompt.lastIndexOf('Findings:');
        if (findingsIdx !== -1) {
          // Grab from the last double-newline before "Findings:" to end of original
          const sectionStart = task4Prompt.lastIndexOf('\n\n', findingsIdx);
          const querySection = sectionStart !== -1
            ? task4Prompt.substring(sectionStart)
            : task4Prompt.substring(findingsIdx);
          truncated += '\n\n' + querySection;
          console.log(`📏 [Task 4] Re-appended query section (${querySection.length} chars)`);
        }
      }

      task4Prompt = truncated;
      console.log(`📏 [Task 4] Final truncated prompt: ${task4Prompt.length} chars (~${Math.round(task4Prompt.length / CHARS_PER_TOKEN)} tokens)`);
    }
    
    // DIAGNOSTIC LOGGING:
    console.groupCollapsed('🕵️‍♂️ Full Task #4 Prompt to Mobile Qwen3.5');
    console.log(task4Prompt);
    console.groupEnd();

    let fullResponse = '';
    
    const promptForGeneration = task4UserPrompt
      ? this.buildMessages(task4PromptOrSystem, task4Prompt)
      : task4Prompt;

    // Generate the enhanced query using Mobile Qwen3.5
    for await (const result of this.generateStream(promptForGeneration, {
      temperature: 0.6, 
      topP: 0.9,
      maxTokens: this.taskMaxTokens('task4')
    })) {
      fullResponse += result.token;
    }
  
    console.log('✅ Task #4 generation complete');
    console.log(`📄 Raw response length: ${fullResponse.length} chars`);
    // Post-process the response to remove erroneous sections and Qwen think tags.
    // The model is incorrectly adding "## Conclusion" and "## Final Answer".
    // We will split the response at the first occurrence of "## Conclusion" and take everything before it.
    const cleanedResponse = cleanLowLocalQwenOutput(
      fullResponse.split("\n## Conclusion")[0]
    );

    console.log(`📄 Cleaned profile insights: ${cleanedResponse.substring(0, 200)}...`);
    
    // DIAGNOSTIC:
    console.groupCollapsed('🕵️‍♂️ Full Raw Task #4 Output from Mobile Qwen3.5');
    console.log(fullResponse);
    console.groupEnd();

    // Return the CLEANED response, not the raw one
    return cleanedResponse;
  }

  /**
   * Execute Task #5: Generate an enhanced query from facts.
   *
   * @param task5Prompt - The prompt from the backend containing the facts from Task 4.
   * @returns The raw LLM response (the final enhanced query string).
   */
  public async executeTask5(task5PromptOrSystem: string, task5UserPrompt?: string): Promise<string> {
    const task5Prompt = task5UserPrompt || task5PromptOrSystem;
    this.assertPromptReady('Task 5', task5Prompt);
    console.log('🚀 Executing Task #5: Enhanced Query Generation');
    console.log(`📝 Prompt length: ${task5Prompt.length} chars`);
  
    // DIAGNOSTIC LOGGING:
    console.groupCollapsed('🕵️‍♂️ Full Task #5 Prompt to Mobile Qwen3.5');
    console.log(task5Prompt);
    console.groupEnd();
  
    let fullResponse = '';
    
    const promptForGeneration = this.buildMessages(task5PromptOrSystem, task5UserPrompt);

    // Generate the enhanced query using Mobile Qwen3.5
    for await (const result of this.generateStream(promptForGeneration, {
      temperature: 0.7, 
      topP: 0.9,
      maxTokens: this.taskMaxTokens('task5')
    })) {
      fullResponse += result.token;
    }
  
    const cleaned = cleanLowLocalQwenOutput(fullResponse);

    console.log('✅ Task #5 generation complete');
    console.log(`📄 Enhanced query: ${cleaned.substring(0, 200)}...`);
    
    console.log('🕵️‍♂️ Full Raw Task #5 Output from Mobile Qwen3.5');
    console.log(fullResponse.trim());

    // Return the cleaned response, which is the enhanced query
    return cleaned;
  }

  /**
   * Execute Task #6: Structured follow-up triage.
   *
   * The backend now asks Mobile Qwen3.5 to return a JSON object like:
   * {
   *   "decision": "NO_ACTION | AUTO_RESPOND_WITH_CONTEXT | WAIT_FOR_USER",
   *   "requested_info": "...",
   *   "retrieval_query": "...",
   *   "confidence": 0.0,
   *   "rationale_short": "..."
   * }
   *
   * @param prompt - The combined structured Task 6 prompt from the backend.
   * @returns The raw model output, usually a JSON object string.
   */
  public async executeTask6(promptOrSystem: string, userPrompt?: string): Promise<string> {
    const prompt = userPrompt || promptOrSystem;
    this.assertPromptReady('Task 6', prompt);
    console.log('🚀 [Qwen3.5 Mobile] Executing Task #6: Structured Follow-Up Triage');
    console.log(`📝 Prompt length: ${prompt.length} chars`);

    // DIAGNOSTIC LOGGING:
    console.groupCollapsed('🕵️‍♂️ Full Task #6 Prompt to Mobile Qwen3.5');
    console.log(prompt);
    console.groupEnd();

    let fullResponse = '';

    const promptForGeneration = this.buildMessages(promptOrSystem, userPrompt);

    // JSON output needs more than 10 tokens. Keep this small, but large enough
    // for the structured decision object. The frontend parser will validate and
    // fail closed if the output is malformed.
    for await (const result of this.generateStream(promptForGeneration, {
      temperature: 0.1,
      topP: 0.9,
      maxTokens: this.taskMaxTokens('task6')
    })) {
      fullResponse += result.token || '';
    }

    const cleaned = fullResponse
      .replace(/<think>[\s\S]*?<\/think>/gi, '')
      .trim();

    console.log(`✅ [Qwen3.5 Mobile] Task #6 raw result: "${cleaned}"`);

    // IMPORTANT: Do not uppercase and do not collapse to GOLDEN/FOLLOW_UP.
    // ChatbotTool.tsx parseTask6Decision(...) now owns validation, JSON parsing,
    // legacy fallback parsing, and fail-closed behavior.
    return cleaned;
  }

  public async interruptGeneration(): Promise<void> {
    if (this.engine) {
      console.log('🛑 Interrupting Qwen3.5 generation...');
      await this.engine.interruptGenerate();
    }
  }

}

